/**
 * Progress and cancellation — the two things every first-pass stage reports on.
 *
 * WHY IT IS ITS OWN MODULE
 * ------------------------
 * `firstPass.ts` used to own these, and both pipelines are downstream of it.
 * Once the aligned path moved into `services/pipeline/` and the legacy path into
 * `services/legacy/`, either of them importing the progress shapes from
 * `firstPass` would have closed an import cycle — and for `legacy/` it would
 * have been an upward edge out of the quarantine as well. They are pure shapes
 * and two one-line functions, so they live at the bottom instead.
 *
 * `firstPass.ts` re-exports the three public types unchanged, so the Tasks
 * page's import line is untouched.
 */

export type FirstPassPhase =
  | 'decoding'
  | 'preparing'
  | 'transcribing'
  | 'slicing'
  | 'saving'
  /** Clip QA — the clips exist and are safe; this stage only polishes them. */
  | 'qa'
  | 'done'
  | 'error';

export interface FirstPassProgress {
  phase: FirstPassPhase;
  /** Short line to show under the task card. */
  message: string;
  /** Overall completion, 0..1. Monotonic across phases. */
  ratio: number;
  /** Populated during `slicing` and `qa`. */
  done?: number;
  total?: number;
}

export type ProgressCallback = (progress: FirstPassProgress) => void;

/*
 * Where each phase starts on the 0..1 bar. QA gets a third of the bar because
 * on a real recording it is a third of the wall-clock time: one Gemini round
 * trip per clip, a few at a time.
 */
export const PHASE_START: Record<FirstPassPhase, number> = {
  decoding: 0.02,
  preparing: 0.08,
  transcribing: 0.15,
  slicing: 0.5,
  saving: 0.62,
  qa: 0.66,
  done: 1,
  error: 1,
};

export function report(cb: ProgressCallback | undefined, progress: FirstPassProgress): void {
  cb?.(progress);
}

export function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new Error('First pass cancelled.');
}
